import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '@/api/client';
import { Icon, useToast } from '@/design-system';
import { variantsKey } from '@/features/variants/api';
import { errorText } from '@/shell/errorText';
import { followEdit, messagesKey, messagesQuery, revisionsKey, sendChat, type MessageOut, type ToolChip } from './api';
import type { DesignerCtx } from './DesignerTab';
import s from './panels.module.css';

/** The edit running now: its scope, and the tools and answer streamed so far. */
type Live = { section: string | null; tools: ToolChip[]; text: string };

function Tools({ tools }: { tools: ToolChip[] }) {
  if (!tools.length) return null;
  return (
    <div className={s.tools}>
      {tools.map((c, i) => (
        <span key={i} className={s.tool} data-error={c.error || undefined} title={c.result_summary ?? undefined}>
          <Icon name="wrench" size={10} />
          {[c.name, c.target].filter(Boolean).join(' ')}
        </span>
      ))}
    </div>
  );
}

/**
 * «AI-чат · Claude» («MCP · scoped»): the PM's requests (light, right) and the AI's answers (dark,
 * left) with their mono tool chips above and «→ revision rN» below; a failed edit's system line; while
 * an edit runs, its answer streams in (`job.ai_tool` chips, `job.ai_text` chunks) above the busy line;
 * suggestion chips; the composer (Enter sends, Shift+Enter adds a line). An edit is scoped to the
 * selected section, or the element inside it (C3), and is based on the current head (409
 * `revision_conflict` when the variant changed meanwhile).
 */
export function ChatPanel({ ctx, fullscreen }: { ctx: DesignerCtx; fullscreen: boolean }) {
  const { site, variant, selection } = ctx;
  const { t } = useTranslation('designer');
  const { t: tc } = useTranslation('common');
  const qc = useQueryClient();
  const toast = useToast();
  const messages = useQuery(messagesQuery(variant.id)).data ?? [];
  const [draft, setDraft] = useState('');
  const [live, setLive] = useState<Live | null>(null);
  const log = useRef<HTMLDivElement>(null);
  // Set by a send: the conversation then follows the new messages (on open it starts at the top, as the design).
  const follow = useRef(false);
  const close = useRef<(() => void) | null>(null);
  const chips = t('chat.chips', { returnObjects: true }) as string[];
  const tt = tc as unknown as (k: string, o?: Record<string, unknown>) => string;

  useEffect(() => () => close.current?.(), []);
  useEffect(() => {
    if (follow.current && log.current) log.current.scrollTop = log.current.scrollHeight;
  }, [messages.length, live]);

  const send = async () => {
    const text = draft.trim();
    if (!text || live) return;
    const section = selection.section;
    setDraft('');
    follow.current = true;
    setLive({ section, tools: [], text: '' });
    try {
      const out = await sendChat(variant.id, {
        message: text,
        selection: section ? { section_id: section, el_path: selection.el ?? null } : null,
        base_revision_id: variant.head?.id ?? null,
      });
      qc.setQueryData<MessageOut[]>(messagesKey(variant.id), (m) => [...(m ?? []), out.message]);
      const edit = followEdit(out.job_id, {
        tool: (chip) => setLive((l) => l && { ...l, tools: [...l.tools, chip] }),
        text: (chunk) => setLive((l) => l && { ...l, text: l.text + chunk }),
      });
      close.current = edit.close;
      const end = await edit.done;
      if (end.status === 'succeeded' && end.result.revision === null) toast(t('chat.nothing_changed'));
      else if (end.error) throw end.error;
    } catch (e) {
      toast(e instanceof ApiError && e.code === 'revision_conflict' ? t('chat.conflict') : t('chat.failed', { error: errorText(e, tc) }));
    } finally {
      close.current = null;
      setLive(null);
      void qc.invalidateQueries({ queryKey: messagesKey(variant.id) });
      void qc.invalidateQueries({ queryKey: revisionsKey(variant.id) });
      void qc.invalidateQueries({ queryKey: variantsKey(site.id) });
    }
  };
  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void send();
    }
  };

  return (
    <section className={s.chat} data-fs={fullscreen} aria-label={t('chat.title')}>
      <div className={s.chatHead}>
        <h2 className={s.chatTitle}>
          <Icon name="sparkles" size={16} className={s.sparkle} />
          {t('chat.title')}
        </h2>
        <span className={s.scope}>{t('chat.scope')}</span>
      </div>
      <div ref={log} className={s.log} role="log" aria-label={t('chat.log')}>
        {messages.map((m) =>
          m.role === 'pm' ? (
            <div key={m.id} className={s.pm}>
              {m.content}
            </div>
          ) : m.role === 'system' ? (
            // A failed edit: the content is the error code.
            <div key={m.id} className={s.system}>
              {t('chat.system', { reason: tt(`errors.${m.content}`, { defaultValue: m.content }) })}
            </div>
          ) : (
            <div key={m.id} className={s.ai}>
              <Tools tools={m.tools} />
              <div className={s.aiText}>{m.content}</div>
              {m.revision && <span className={s.rev}>{t('chat.rev', { n: m.revision.number })}</span>}
            </div>
          ),
        )}
        {live && (live.tools.length > 0 || live.text) && (
          <div className={s.ai} data-live>
            <Tools tools={live.tools} />
            {live.text && <div className={s.aiText}>{live.text}</div>}
          </div>
        )}
        {live && (
          <div className={s.busy} aria-live="polite">
            <span className={s.spinner} />
            {live.section ? t('chat.busy', { section: live.section }) : t('chat.busy_page')}
          </div>
        )}
      </div>
      <div className={s.composer}>
        <div className={s.chips} role="group" aria-label={t('chat.chips_label')}>
          {chips.map((c) => (
            <button key={c} type="button" className={s.chip} onClick={() => setDraft(c)}>
              {c}
            </button>
          ))}
        </div>
        <div className={s.inputRow}>
          <textarea className={s.input} rows={2} aria-label={t('chat.input')} placeholder={t('chat.placeholder')} value={draft} maxLength={4000} onChange={(e) => setDraft(e.target.value)} onKeyDown={onKey} />
          <button type="button" className={s.send} title={t('chat.send')} aria-label={t('chat.send')} disabled={live !== null} onClick={() => void send()}>
            <Icon name="arrow-up" size={17} />
          </button>
        </div>
      </div>
    </section>
  );
}
